import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, Trash2, Edit, Eye, X, ChevronDown, ChevronUp, Image, Loader2 } from 'lucide-react';
import ResponsiveImage from './common/ResponsiveImage';
import MuscleBadges from './common/MuscleBadges';
import { useAuth } from '../contexts/AuthContext';
import usePaginatedExercises from '../hooks/usePaginatedExercises';

// Componente per visualizzare il dettaglio completo di un esercizio
const ExerciseDetails = ({ exercise, onClose }) => {
    if (!exercise) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{exercise.nome}</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-white/65 dark:hover:text-gray-200"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    {/* Badge muscoli se disponibili */}
                    {exercise.primary_muscle ? (
                        <MuscleBadges 
                            primaryMuscle={exercise.primary_muscle}
                            secondaryMuscles={exercise.secondary_muscles}
                            variant="compact"
                        />
                    ) : (
                        <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white/85 rounded-md text-sm">
                            {exercise.gruppo_muscolare}
                        </span>
                    )}
                    {(exercise.is_cardio === 1 || exercise.is_cardio === '1') && (
                        <span className="inline-block px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-full text-xs">
                            Cardio
                        </span>
                    )}
                </div>
                {exercise.attrezzatura && (
                    <div className="text-sm text-gray-500 dark:text-white/65">
                        🔧 {exercise.attrezzatura}
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        {(exercise.immagine_nome || exercise.immagine_statica) ? (
                            <div className="mb-4 rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 select-none" onContextMenu={(e) => e.preventDefault()}>
                                <img
                                    src={exercise.immagine_nome ? `/uploads/images/${exercise.immagine_nome}` : exercise.immagine_statica}
                                    alt={exercise.nome}
                                    className="w-full h-auto max-h-64 object-contain pointer-events-auto"
                                    onContextMenu={(e) => e.preventDefault()}
                                    draggable={false}
                                />
                            </div>
                        ) : (
                            <div className="mb-4 flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <Image className="h-12 w-12 text-gray-400" />
                            </div>
                        )}

                        <div className="mb-4">
                            {/* Badge muscoli */}
                            {exercise.primary_muscle ? (
                                <MuscleBadges 
                                    primaryMuscle={exercise.primary_muscle}
                                    secondaryMuscles={exercise.secondary_muscles}
                                    variant="expanded"
                                    showCategory={true}
                                />
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-sm font-medium">
                                        {exercise.gruppo_muscolare}
                                    </span>
                                </div>
                            )}
                            
                            {/* Attrezzatura */}
                            {exercise.attrezzatura && (
                                <div className="mt-3">
                                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-white/85 text-sm font-medium">
                                        🔧 {exercise.attrezzatura}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <h4 className="text-sm font-medium text-gray-500 dark:text-white/65 uppercase tracking-wider mb-1">
                                Descrizione
                            </h4>
                            <p className="text-gray-700 dark:text-white/85 whitespace-pre-line">
                                {exercise.descrizione || "Nessuna descrizione disponibile."}
                            </p>
                        </div>

                        <div>
                            <h4 className="text-sm font-medium text-gray-500 dark:text-white/65 uppercase tracking-wider mb-1">
                                Attrezzatura
                            </h4>
                            <p className="text-gray-700 dark:text-white/85">
                                {exercise.attrezzatura || "Nessuna attrezzatura richiesta"}
                            </p>
                        </div>

                        {exercise.created_at && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-500 dark:text-white/65 uppercase tracking-wider mb-1">
                                    Creato il
                                </h4>
                                <p className="text-gray-700 dark:text-white/85">
                                    {new Date(exercise.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ExerciseList = ({ esercizi: propEsercizi = [], onDelete, onEdit, infiniteScrollElements, useInternalLoading = false }) => {
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();
    
    const [searchTerm, setSearchTerm] = useState('');
    const [localSearchInput, setLocalSearchInput] = useState(''); // WEBAPP-GYM-001: Input locale per digitazione
    const [filters, setFilters] = useState({
        equipment: '',
        primaryMuscle: ''
    });
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [expandedExercises, setExpandedExercises] = useState({});
    
    // Stato per filtri da API
    const [filterOptions, setFilterOptions] = useState({
        equipments: [],
        primaryMuscles: []
    });
    const [loadingFilters, setLoadingFilters] = useState(true);
    
    // Hook per caricamento esercizi (solo se useInternalLoading è true)
    const {
        exercises: internalExercises,
        loading: loadingExercises,
        initialLoading: initialLoadingExercises,
        error: exercisesError,
        hasMore,
        loadMore,
        search: searchExercises
    } = usePaginatedExercises({
        initialLimit: 50,
        enableInfiniteScroll: true
    });
    
    // Usa esercizi interni o prop
    const esercizi = useInternalLoading ? internalExercises : propEsercizi;
    const loading = useInternalLoading ? loadingExercises : false;
    const initialLoading = useInternalLoading ? initialLoadingExercises : false;
    const error = useInternalLoading ? exercisesError : null;
    
    // Carica filtri da API - ricarica quando cambiano i filtri attivi o la ricerca
    useEffect(() => {
        const loadFilters = async () => {
            try {
                setLoadingFilters(true);
                
                // Passa i filtri attivi all'API per ottenere solo le opzioni rilevanti
                const params = {};
                if (filters.equipment && filters.equipment.trim() !== '') {
                    params.attrezzatura = filters.equipment.trim();
                }
                if (filters.primaryMuscle && filters.primaryMuscle.trim() !== '') {
                    params.primary_muscle = filters.primaryMuscle.trim();
                }
                if (searchTerm && searchTerm.trim().length >= 2) {
                    params.search = searchTerm.trim();
                }
                
                const response = await api.get('/exercise_filters.php', { params });
                if (response.data && response.data.success) {
                    setFilterOptions({
                        equipments: response.data.equipments || [],
                        primaryMuscles: response.data.primary_muscles || []
                    });
                } else {
                    console.error('Risposta filtri non valida:', response.data);
                }
            } catch (err) {
                console.error('Errore nel caricamento dei filtri:', err);
                console.error('Dettagli errore:', err.response?.data);
            } finally {
                setLoadingFilters(false);
            }
        };
        
        loadFilters();
    }, [api, filters.equipment, filters.primaryMuscle, searchTerm]);
    
    // Carica esercizi quando c'è ricerca O filtri attivi
    // I filtri vengono combinati (AND) - tutti i filtri attivi vengono applicati insieme
    // Esempio: "Curl" + "Manubri" + "Bicipiti" → trova solo esercizi che hanno tutti e 3 i criteri
    useEffect(() => {
        if (!useInternalLoading) return;
        
        const searchQuery = searchTerm.trim();
        const hasSearch = searchQuery.length >= 2;
        const hasFilters = filters.equipment || filters.primaryMuscle;
        
        // Carica se c'è ricerca (almeno 2 caratteri) O se ci sono filtri attivi
        // Tutti i filtri vengono inviati insieme all'API per essere combinati con AND
        // - Se cerco "Curl" → carica esercizi con "Curl" nel nome
        // - Se seleziono attrezzatura "Manubri" → carica esercizi con "Manubri"
        // - Se cerco "Curl" + attrezzatura "Manubri" → carica esercizi con "Curl" nel nome E "Manubri" come attrezzatura
        if (hasSearch || hasFilters) {
            // Costruisci opzioni filtri - passa solo se non sono stringhe vuote
            const filterOptions = {};
            if (filters.equipment && filters.equipment.trim() !== '') {
                filterOptions.equipment = filters.equipment.trim();
            }
            if (filters.primaryMuscle && filters.primaryMuscle.trim() !== '') {
                filterOptions.primaryMuscle = filters.primaryMuscle.trim();
            }
            
            searchExercises(hasSearch ? searchQuery : '', filterOptions);
        }
    }, [searchTerm, filters.equipment, filters.primaryMuscle, useInternalLoading, searchExercises]);

    // Filtra gli esercizi in base a ricerca e filtri
    // Se useInternalLoading è true, i filtri sono già applicati lato server, quindi restituiamo direttamente
    const filteredExercises = useMemo(() => {
        if (useInternalLoading) {
            // Se usiamo caricamento interno, i filtri sono già applicati lato server
            // Restituiamo direttamente gli esercizi (già filtrati)
            return esercizi;
        } else {
            // Filtraggio completo lato client
            return esercizi.filter(exercise => {
                // Filtro di ricerca testuale
                const matchesSearch =
                    !searchTerm ||
                    exercise.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (exercise.descrizione && exercise.descrizione.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (exercise.gruppo_muscolare && exercise.gruppo_muscolare.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (exercise.attrezzatura && exercise.attrezzatura.toLowerCase().includes(searchTerm.toLowerCase()));

                // Filtro attrezzatura
                const matchesEquipment =
                    !filters.equipment ||
                    (exercise.attrezzatura && exercise.attrezzatura === filters.equipment);

                // Filtro muscolo primario
                const matchesPrimaryMuscle =
                    !filters.primaryMuscle ||
                    (exercise.primary_muscle && exercise.primary_muscle.name === filters.primaryMuscle) ||
                    (!exercise.primary_muscle && exercise.gruppo_muscolare === filters.primaryMuscle);

                return matchesSearch && matchesEquipment && matchesPrimaryMuscle;
            });
        }
    }, [esercizi, searchTerm, filters, useInternalLoading]);

    // Apri/chiudi i dettagli di un esercizio
    const toggleExerciseExpand = (id) => {
        setExpandedExercises(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Controlla se ci sono filtri attivi
    const hasActiveFilters = filters.equipment || filters.primaryMuscle;
    const hasSearch = searchTerm.trim().length >= 2; // Almeno 2 caratteri per considerare una ricerca valida
    const hasSearchOrFilters = hasSearch || hasActiveFilters;
    
    // Mostra messaggio iniziale se non c'è ricerca/filtri e non ci sono esercizi caricati
    if (!hasSearchOrFilters && esercizi.length === 0 && !initialLoading && !loading) {
        return (
            <div className="h-full flex flex-col min-h-0">
                {/* Barra di ricerca e filtri sulla stessa riga - FISSA */}
                <div className="flex-shrink-0 mb-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Barra di ricerca */}
                        <div className="flex-1">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={localSearchInput}
                                    onChange={(e) => setLocalSearchInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        // WEBAPP-GYM-001: Avvia ricerca premendo Invio
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            setSearchTerm(localSearchInput);
                                        }
                                    }}
                                    placeholder="Cerca esercizi e premi Invio..."
                                    className="w-full pl-10 pr-20 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1">
                                    {/* WEBAPP-GYM-001: Bottone per avviare la ricerca */}
                                    <button
                                        type="button"
                                        onClick={() => setSearchTerm(localSearchInput)}
                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                        title="Cerca"
                                    >
                                        <Search className="h-5 w-5" />
                                    </button>
                                    {localSearchInput && (
                                        <button
                                            onClick={() => {
                                                setLocalSearchInput('');
                                                setSearchTerm('');
                                            }}
                                            title="Cancella"
                                        >
                                            <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Filtri sulla stessa riga */}
                        <div className="flex gap-4">
                            {loadingFilters ? (
                                <div className="flex items-center justify-center py-2">
                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                </div>
                            ) : (
                                <>
                                    {/* Filtro Attrezzatura */}
                                    <div className="min-w-[180px]">
                                        <select
                                            value={filters.equipment}
                                            onChange={(e) => setFilters(prev => ({ ...prev, equipment: e.target.value }))}
                                            className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Tutte le attrezzature</option>
                                            {filterOptions.equipments.map(equipment => (
                                                <option key={equipment} value={equipment}>{equipment}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Filtro Muscolo Primario */}
                                    <div className="min-w-[180px]">
                                        <select
                                            value={filters.primaryMuscle}
                                            onChange={(e) => setFilters(prev => ({ ...prev, primaryMuscle: e.target.value }))}
                                            className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Tutti i muscoli</option>
                                            {filterOptions.primaryMuscles.map(muscle => (
                                                <option key={muscle} value={muscle}>{muscle}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-gray-500 dark:text-white/65 mb-2">Inizia a cercare o applica dei filtri per vedere gli esercizi</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500">Digita nella barra di ricerca o seleziona un filtro per iniziare</p>
                    </div>
                </div>
            </div>
        );
    }

    // Se infiniteScrollElements è passato, il container padre gestisce lo scroll
    // Altrimenti ExerciseList gestisce lo scroll internamente
    const hasExternalScroll = !!infiniteScrollElements;

    return (
        <div className="h-full flex flex-col min-h-0">
            {/* Barra di ricerca e filtri sulla stessa riga - FISSA */}
            <div className="flex-shrink-0 mb-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Barra di ricerca */}
                    <div className="flex-1">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={localSearchInput}
                                onChange={(e) => setLocalSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    // WEBAPP-GYM-001: Avvia ricerca premendo Invio
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setSearchTerm(localSearchInput);
                                    }
                                }}
                                placeholder="Cerca esercizi e premi Invio..."
                                className="w-full pl-10 pr-20 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1">
                                {/* WEBAPP-GYM-001: Bottone per avviare la ricerca */}
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm(localSearchInput)}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                    title="Cerca"
                                >
                                    <Search className="h-5 w-5" />
                                </button>
                                {localSearchInput && (
                                    <button
                                        onClick={() => {
                                            setLocalSearchInput('');
                                            setSearchTerm('');
                                        }}
                                        title="Cancella"
                                    >
                                        <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Filtri sulla stessa riga */}
                    <div className="flex gap-4">
                        {loadingFilters ? (
                            <div className="flex items-center justify-center py-2">
                                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                            </div>
                        ) : (
                            <>
                                {/* Filtro Attrezzatura */}
                                <div className="min-w-[180px]">
                                    <select
                                        value={filters.equipment}
                                        onChange={(e) => setFilters(prev => ({ ...prev, equipment: e.target.value }))}
                                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                        disabled={loadingFilters}
                                    >
                                        <option value="">Tutte le attrezzature</option>
                                        {filterOptions.equipments.map(equipment => (
                                            <option key={equipment} value={equipment}>{equipment}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Filtro Muscolo Primario */}
                                <div className="min-w-[180px]">
                                    <select
                                        value={filters.primaryMuscle}
                                        onChange={(e) => setFilters(prev => ({ ...prev, primaryMuscle: e.target.value }))}
                                        className="w-full p-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500"
                                        disabled={loadingFilters}
                                    >
                                        <option value="">Tutti i muscoli</option>
                                        {filterOptions.primaryMuscles.map(muscle => (
                                            <option key={muscle} value={muscle}>{muscle}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Reset filtri se attivi */}
                                {hasActiveFilters && (
                                    <button
                                        onClick={() => setFilters({ equipment: '', primaryMuscle: '' })}
                                        className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 transition-colors whitespace-nowrap"
                                    >
                                        Reset
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading iniziale - solo se c'è ricerca o filtri attivi */}
            {initialLoading && hasSearchOrFilters && (
                <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3 text-gray-500 dark:text-white/85">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <span>Caricamento esercizi...</span>
                    </div>
                </div>
            )}

            {/* Errore */}
            {error && hasSearch && (
                <div className="flex-1 flex items-center justify-center">
                    <div className="p-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                        {error}
                    </div>
                </div>
            )}

            {/* Contenuto principale */}
            {(!initialLoading || !hasSearchOrFilters) && !error && (
                <>
                    {/* Contatore risultati - FISSO */}
                    <div className="flex-shrink-0 mb-4 text-sm text-gray-600 dark:text-white/65">
                        Trovati {filteredExercises.length} esercizi
                    </div>

                    {/* Griglia esercizi stile Instagram - SCROLLABILE (solo se non c'è scroll esterno) */}
                    <div className={hasExternalScroll ? "" : "flex-1 min-h-0 overflow-y-auto"}>
                        {filteredExercises.length === 0 ? (
                            <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                                <p className="text-gray-500 dark:text-white/65">Nessun esercizio trovato con i filtri selezionati</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                {filteredExercises.map(exercise => (
                                    <div
                                        key={exercise.id}
                                        className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                                    >
                                        {/* Immagine grande */}
                                        <div className="relative aspect-square bg-gray-100 dark:bg-gray-800 overflow-hidden group">
                                            {(exercise.immagine_nome || exercise.immagine_statica) ? (
                                                <div 
                                                    className="w-full h-full cursor-pointer select-none"
                                                    onClick={() => setSelectedExercise(exercise)}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                >
                                                    <img
                                                        src={exercise.immagine_nome ? `/api/serve_exercise_gif.php?filename=${encodeURIComponent(exercise.immagine_nome)}` : (exercise.immagine_statica ? `/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_statica)}&type=image` : '')}
                                                        alt={exercise.nome}
                                                        className="w-full h-full object-cover"
                                                        onContextMenu={(e) => e.preventDefault()}
                                                        draggable={false}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            const placeholder = e.target.nextElementSibling;
                                                            if (placeholder) placeholder.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="hidden w-full h-full items-center justify-center">
                                                        <Image className="h-16 w-16 text-gray-400" />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Image className="h-16 w-16 text-gray-400" />
                                                </div>
                                            )}
                                            {/* Overlay con pulsanti al hover */}
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedExercise(exercise);
                                                    }}
                                                    className="p-2 bg-white/90 dark:bg-gray-900/90 rounded-full text-gray-700 dark:text-white/90 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                                    title="Visualizza dettagli"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                {onEdit && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onEdit(exercise);
                                                        }}
                                                        className="p-2 bg-white/90 dark:bg-gray-900/90 rounded-full text-blue-600 dark:text-blue-400 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                                        title="Modifica esercizio"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {onDelete && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDelete(exercise.id);
                                                        }}
                                                        className="p-2 bg-white/90 dark:bg-gray-900/90 rounded-full text-red-600 dark:text-red-400 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                                        title="Elimina esercizio"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Nome e info sotto l'immagine */}
                                        <div className="p-3">
                                            <h3 
                                                className="font-medium text-gray-900 dark:text-white text-sm mb-2 line-clamp-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                                                onClick={() => setSelectedExercise(exercise)}
                                                title={exercise.nome}
                                            >
                                                {exercise.nome}
                                            </h3>
                                            
                                            {/* Badge muscoli/attrezzatura compatti */}
                                            <div className="space-y-1">
                                                {exercise.primary_muscle ? (
                                                    <div className="text-xs text-gray-600 dark:text-white/65 truncate">
                                                        {exercise.primary_muscle.name}
                                                    </div>
                                                ) : exercise.gruppo_muscolare ? (
                                                    <div className="text-xs text-gray-600 dark:text-white/65 truncate">
                                                        {exercise.gruppo_muscolare}
                                                    </div>
                                                ) : null}
                                                
                                                <div className="flex flex-wrap items-center gap-1">
                                                    {(exercise.is_cardio === 1 || exercise.is_cardio === '1') && (
                                                        <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded">
                                                            Cardio
                                                        </span>
                                                    )}
                                                    {exercise.attrezzatura && (
                                                        <span className="text-xs text-gray-500 dark:text-white/65 truncate" title={exercise.attrezzatura}>
                                                            🔧 {exercise.attrezzatura.length > 12 ? exercise.attrezzatura.substring(0, 12) + '...' : exercise.attrezzatura}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Elementi di infinite scroll passati come prop */}
                        {infiniteScrollElements && (
                            <div className="col-span-full">
                                {infiniteScrollElements}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Modal dettagli esercizio */}
            {selectedExercise && (
                <ExerciseDetails
                    exercise={selectedExercise}
                    onClose={() => setSelectedExercise(null)}
                />
            )}
        </div>
    );
};

export default ExerciseList;